import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { TextField } from '@/global/components/TextField'
import { Calendar } from '@/global/components/ui/calendar'
import { MonthCalendar } from '@/global/components/ui/month-calendar'
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
    PopoverTrigger,
} from '@/global/components/ui/popover'
import { TimePicker } from '@/global/components/ui/time-picker'
import { cn } from '@/lib/utils'
import type { CleaveOptions } from 'cleave.js/options'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import { CalendarIcon } from 'lucide-react'
import { type ChangeEvent, type ReactNode, useRef, useState } from 'react'

type ViewData = 'date' | 'month' | 'datetime'

/** Formato do texto digitado — é também o padrão de exibição de cada view. */
const FORMATO_POR_VIEW: Record<ViewData, string> = {
    date: 'DD/MM/YYYY',
    month: 'MM/YYYY',
    datetime: 'DD/MM/YYYY HH:mm',
}

/** Máscara do cleave equivalente ao formato acima. */
const MASCARA_POR_VIEW: Record<ViewData, CleaveOptions> = {
    date: { date: true, datePattern: ['d', 'm', 'Y'], delimiter: '/' },
    month: { date: true, datePattern: ['m', 'Y'], delimiter: '/' },
    // O `date` do cleave não combina data e hora no mesmo campo; blocos crus
    // desenham `DD/MM/YYYY HH:mm`, e quem valida é o `dayjs` estrito abaixo.
    datetime: {
        numericOnly: true,
        blocks: [2, 2, 4, 2, 2],
        delimiters: ['/', '/', ' ', ':'],
    },
}

/** Granularidade que decide se o valor digitado é o mesmo que já está no campo. */
const UNIDADE_POR_VIEW = {
    date: 'day',
    month: 'month',
    datetime: 'minute',
} as const

export type DateFieldProps = {
    value: Dayjs | null
    /** Dispensável só em campo `readOnly`, que existe para exibir a data. */
    onChange?: (value: Dayjs | null) => void
    /**
     * `date` abre o grid de dias; `month`, o grid de meses; `datetime`, o grid
     * de dias com as colunas de hora ao lado.
     */
    view?: ViewData
    label?: ReactNode
    /** Formato dayjs do texto exibido. Padrão: `DD/MM/YYYY` (`MM/YYYY` no mês). */
    format?: string
    /** Texto exibido no lugar do valor formatado — desliga a digitação. */
    displayValue?: string
    /**
     * Digitação da data no próprio campo, com máscara. Ligada por padrão;
     * desligue quando o texto do campo não for o valor (ver `podeDigitar`).
     */
    permitirDigitacao?: boolean
    placeholder?: string
    helperText?: ReactNode
    error?: ReactNode | boolean
    unHelperText?: boolean
    required?: boolean
    disabled?: boolean
    /** Campo só de leitura: mostra a data, mas não digita nem abre o calendário. */
    readOnly?: boolean
    size?: 'small' | 'medium'
    variant?: 'filled' | 'outlined'
    /** Classe do wrapper (largura, coluna, `hidden`). */
    className?: string
    /** Classe da caixa do campo — é o `inputClassName` do `TextField`. */
    inputClassName?: string
    /** Conteúdo à esquerda do valor — o `startAdornment` do `TextField`. */
    startAdornment?: ReactNode
    /**
     * Conteúdo à direita do valor. Entra **antes** do botão do calendário, que
     * continua sendo o último elemento da caixa.
     *
     * Vai embrulhado em um `group` com `aria-disabled`, como no `FormField`: é
     * o que faz o `IconeAjuda` parar de responder quando o campo está
     * bloqueado (`group-aria-disabled:pointer-events-none`).
     */
    endAdornment?: ReactNode
    autoComplete?: string
    id?: string
    name?: string
    'data-testid'?: string
}

/**
 * Campo de data: `TextField` com máscara + popover com calendário.
 *
 * Digita-se a data:
 * o texto fica em estado local enquanto está incompleto e só vira `onChange`
 * quando forma uma data válida. O calendário abre pelo ícone, não pelo clique
 * na caixa — o resto do campo é para digitar.
 *
 * Quem converte `Date` ↔ `Dayjs` é este componente — as peças `ui/calendar` e
 * `ui/month-calendar` falam `Date`/`Dayjs` cru.
 *
 * O calendário é ancorado (`PopoverAnchor`) na caixa inteira do campo, mas o
 * `PopoverTrigger` é só o botão do ícone: no root do `TextField` o trigger
 * injetaria `type="button"` no `<input>` e o transformaria em botão.
 */
export function DateField({
    value,
    onChange,
    view = 'date',
    label,
    format,
    displayValue,
    permitirDigitacao = true,
    placeholder,
    helperText,
    error,
    unHelperText,
    required,
    disabled,
    readOnly,
    size = 'medium',
    variant = 'filled',
    className,
    inputClassName,
    startAdornment,
    endAdornment,
    autoComplete,
    id,
    name,
    'data-testid': dataTestId,
}: DateFieldProps) {
    const [aberto, setAberto] = useState(false)
    const campoRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

    const bloqueado = !!readOnly || !!disabled

    // Um `Dayjs` inválido formata como `Invalid Date`; para o campo isso é o
    // mesmo que não ter valor.
    const valor = value?.isValid() ? value : null
    const unidade = UNIDADE_POR_VIEW[view]
    const formatoMascara = FORMATO_POR_VIEW[view]
    const formatoExibicao = format ?? formatoMascara

    const textoDoValor =
        displayValue ??
        (valor ? valor.locale('pt-br').format(formatoExibicao) : '')

    /**
     * Só se digita quando o texto do campo é o valor no formato da máscara —
     * caso contrário a máscara destruiria o que está escrito e o que fosse
     * digitado não teria como voltar como valor. Fica de fora, então: campo
     * bloqueado, `displayValue` (texto próprio do call-site), formato de
     * exibição diferente do da máscara e quem pede `permitirDigitacao={false}`.
     */
    const podeDigitar =
        !bloqueado &&
        permitirDigitacao &&
        displayValue === undefined &&
        formatoExibicao === formatoMascara

    // Texto em edição. Enquanto está incompleto não vira `onChange`, mas o
    // valor de fora continua mandando: quando ele muda, o texto acompanha.
    const [texto, setTexto] = useState(textoDoValor)
    const [textoSincronizado, setTextoSincronizado] = useState(textoDoValor)
    if (textoDoValor !== textoSincronizado) {
        setTextoSincronizado(textoDoValor)
        setTexto(textoDoValor)
    }

    const digitar = (evento: ChangeEvent<HTMLInputElement>) => {
        const novoTexto = evento.target.value
        setTexto(novoTexto)

        if (novoTexto === '') {
            if (valor) onChange?.(null)
            return
        }

        const data = dayjs(novoTexto, formatoMascara, true)
        // Data incompleta ou impossível: espera o resto da digitação.
        if (!data.isValid()) return
        if (valor?.isSame(data, unidade)) return

        onChange?.(data)
    }

    /** Texto que não formou data volta a ser o último valor válido. */
    const sair = () => {
        if (texto === '') return
        if (dayjs(texto, formatoMascara, true).isValid()) return
        setTexto(textoDoValor)
    }

    const selecionar = (data: Dayjs | null) => {
        // O foco volta para o campo **antes** do `onChange`: há formulários que
        // decidem o que recalcular olhando `document.activeElement.id` durante
        // o render disparado pela mudança (Afastamento recalcula `dias` ou
        // `dataFinal` conforme o campo focado). Sem isto o foco ainda estaria
        // no dia do calendário e o recálculo não aconteceria — o
        // `onCloseAutoFocus` abaixo só devolve o foco depois do render.
        campoRef.current?.focus()
        onChange?.(data)
        setAberto(false)
    }

    const hoje = () =>
        selecionar(view === 'month' ? dayjs().startOf('month') : dayjs())

    /** No modo data-hora o grid de dias mexe só na data; a hora escolhida fica. */
    const comHoraDoValor = (data: Dayjs) =>
        view === 'datetime' && valor
            ? data.hour(valor.hour()).minute(valor.minute())
            : data

    const botaoCalendario = (
        <IconButton
            size="small"
            color="default"
            disabled={bloqueado}
            title="Abrir calendário"
            aria-label="Abrir calendário"
            // O `ui/button` marca o ícone com `pointer-events-none`: o clique
            // só chega aqui, nunca no `<svg>`.
        >
            <CalendarIcon />
        </IconButton>
    )

    return (
        <Popover open={aberto} onOpenChange={setAberto}>
            <PopoverAnchor className={cn('w-full', className)}>
                <TextField
                    ref={campoRef}
                    id={id}
                    name={name}
                    label={label}
                    value={podeDigitar ? texto : textoDoValor}
                    placeholder={placeholder}
                    options={podeDigitar ? MASCARA_POR_VIEW[view] : undefined}
                    onChange={podeDigitar ? digitar : undefined}
                    onBlur={podeDigitar ? sair : undefined}
                    readOnly={!podeDigitar}
                    required={required}
                    disabled={disabled}
                    size={size}
                    variant={variant}
                    error={error}
                    helperText={helperText}
                    unHelperText={unHelperText}
                    inputClassName={inputClassName}
                    autoComplete={autoComplete}
                    data-testid={dataTestId}
                    startAdornment={startAdornment}
                    endAdornment={
                        <>
                            {endAdornment && (
                                <div
                                    aria-disabled={bloqueado}
                                    className="group"
                                >
                                    {endAdornment}
                                </div>
                            )}
                            {bloqueado ? (
                                botaoCalendario
                            ) : (
                                <PopoverTrigger asChild>
                                    {botaoCalendario}
                                </PopoverTrigger>
                            )}
                        </>
                    }
                />
            </PopoverAnchor>
            {!bloqueado && (
                <PopoverContent
                    align="start"
                    arrow={false}
                    collisionPadding={8}
                    // Sem espaço em nenhum dos lados o Radix só vira o lado e
                    // o calendário vaza da tela. Desloca pela falta (altura −
                    // espaço disponível) na direção do campo, cobrindo-o.
                    className={cn(
                        'w-auto p-0',
                        'data-[side=bottom]:[translate:0_min(0px,calc(var(--radix-popover-content-available-height)-100%))]',
                        'data-[side=top]:[translate:0_max(0px,calc(100%-var(--radix-popover-content-available-height)))]',
                    )}
                    // O foco volta para o campo, não para o botão do ícone:
                    // depois de escolher a data quem continua a edição é o
                    // texto.
                    onCloseAutoFocus={evento => {
                        evento.preventDefault()
                        campoRef.current?.focus()
                    }}
                >
                    {view === 'month' ? (
                        <MonthCalendar value={valor} onChange={selecionar} />
                    ) : (
                        <div className="flex">
                            <Calendar
                                mode="single"
                                selected={valor?.toDate()}
                                defaultMonth={valor?.toDate()}
                                onSelect={data =>
                                    selecionar(
                                        data
                                            ? comHoraDoValor(dayjs(data))
                                            : null,
                                    )
                                }
                            />
                            {view === 'datetime' && (
                                <TimePicker
                                    value={valor?.format('HH:mm')}
                                    onChange={hora => {
                                        const [h, m] = hora.split(':')
                                        onChange?.(
                                            (valor ?? dayjs())
                                                .hour(Number(h))
                                                .minute(Number(m))
                                                .second(0),
                                        )
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {/* O mesmo par de ações do seletor nativo do Chrome. */}
                    <div className="flex items-center justify-between border-t">
                        <Button variant="text" onClick={() => selecionar(null)}>
                            Limpar
                        </Button>
                        <Button variant="text" onClick={hoje}>
                            {view === 'datetime' ? 'Agora' : 'Hoje'}
                        </Button>
                    </div>
                </PopoverContent>
            )}
        </Popover>
    )
}
